import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { bikeAPI } from "../../api";
import { Spinner, EmptyState, Pagination } from "../../components/common/Misc";
import styles from "./BikesPage.module.css";

const API_BASE = process.env.REACT_APP_API_URL?.replace("/api", "") || "";
const resolveImg = (url) =>
  url && url.startsWith("http") ? url : `${API_BASE}${url}`;

const TYPES = [
  "",
  "petrol-scooter",
  "e-scooter",
  "petrol-bike",
  "premium-bike",
];
const SORTS = [
  { value: "", label: "Default" },
  { value: "price_asc", label: "Price: Low to High" },
  { value: "price_desc", label: "Price: High to Low" },
  { value: "rating_desc", label: "Top Rated" },
  { value: "newest", label: "Newest" },
];

const TYPE_ICONS = {
  "petrol-scooter": "🛵",
  "e-scooter": "⚡",
  "petrol-bike": "🏍️",
  "premium-bike": "🏆",
};

const TYPE_LABELS = {
  "petrol-scooter": "Petrol Scooter",
  "e-scooter": "E-Scooter",
  "petrol-bike": "Petrol Bike",
  "premium-bike": "Premium Bike",
};

const ScooterIllustration = ({ className }) => (
  <img
    className={className}
    src="/e-scooter-BDdDjGKT.png"
    alt="Motorbike"
    onError={(e) => {
      e.target.style.display = "none";
    }}
  />
);

const BikesPage = () => {
  const [bikes, setBikes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [filters, setFilters] = useState({
    type: "",
    status: "",
    location: "",
    sort: "",
  });

  const fetchBikes = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...filters, page, limit: 12 };
      // Only remove empty strings and null/undefined — keep 0 and numeric values
      Object.keys(params).forEach((k) => {
        const v = params[k];
        if (v === "" || v === null || v === undefined) delete params[k];
      });
      const res = await bikeAPI.getAll(params);
      const data = res?.data?.data;
      setBikes(Array.isArray(data) ? data : []);
      setPages(res?.data?.pages || 1);
    } catch (err) {
      console.error("fetchBikes error:", err);
      setBikes([]);
      setPages(1);
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => {
    fetchBikes();
  }, [fetchBikes]);

  const setFilter = (k, v) => {
    setFilters((f) => ({ ...f, [k]: v }));
    setPage(1);
  };

  return (
    <div>
      {/* Hero banner */}
      <div className={styles.hero}>
        <div className={`container ${styles.heroInner}`}>
          <div className={styles.heroText}>
            <span className={styles.heroBadge}>🏍️ Moto Rentals</span>
            <h1 className={styles.heroTitle}>
              Find your perfect
              <br />
              <span>ride today</span>
            </h1>
            <p className={styles.heroSub}>
              Browse our premium fleet of motorbikes. Book in seconds, ride
              instantly across the city.
            </p>
            <div className={styles.heroStats}>
              <div className={styles.heroStat}>
                <strong>50+</strong>
                <span>Motorbikes</span>
              </div>
              <div className={styles.heroStatDiv} />
              <div className={styles.heroStat}>
                <strong>4.8★</strong>
                <span>Avg rating</span>
              </div>
              <div className={styles.heroStatDiv} />
              <div className={styles.heroStat}>
                <strong>24/7</strong>
                <span>Support</span>
              </div>
            </div>
          </div>
          <div className={styles.heroIllustration}>
            <ScooterIllustration className={styles.heroBike} />
          </div>
        </div>
      </div>

      <div className="container">
        {/* Type pills */}
        <div className={styles.typePills}>
          {TYPES.map((t) => (
            <button
              key={t}
              className={`${styles.pill} ${filters.type === t ? styles.pillActive : ""}`}
              onClick={() => setFilter("type", t)}
            >
              {t ? `${TYPE_ICONS[t]} ${TYPE_LABELS[t]}` : "🏍️ All types"}
            </button>
          ))}
        </div>

        {/* Search + sort bar */}
        <div className={styles.searchBar}>
          <div className={styles.searchWrap}>
            <span className={styles.searchIcon}>📍</span>
            <input
              className={styles.search}
              placeholder="Search by location…"
              value={filters.location}
              onChange={(e) => setFilter("location", e.target.value)}
            />
          </div>
          <select
            className={styles.select}
            value={filters.status}
            onChange={(e) => setFilter("status", e.target.value)}
          >
            <option value="">All statuses</option>
            <option value="available">Available only</option>
            <option value="booked">Booked only</option>
          </select>
          <select
            className={styles.select}
            value={filters.sort}
            onChange={(e) => setFilter("sort", e.target.value)}
          >
            {SORTS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>

        {loading ? (
          <Spinner />
        ) : bikes.length === 0 ? (
          <EmptyState
            icon="🏍️"
            title="No motorbikes found"
            subtitle="Try adjusting your filters"
          />
        ) : (
          <div className={styles.grid}>
            {bikes.map((bike) => (
              <BikeCard key={bike._id} bike={bike} />
            ))}
          </div>
        )}

        <Pagination page={page} pages={pages} onChange={setPage} />
      </div>
    </div>
  );
};

const BikeCard = ({ bike }) => {
  if (!bike) return null;
  const isBooked = bike.status === "booked";
  const rating = Number(bike.avg_rating) || 0;
  const reviews = Number(bike.review_count) || 0;
  const stars = Math.min(5, Math.max(0, Math.round(rating)));
  return (
    <Link
      to={`/bikes/${bike._id}`}
      className={`${styles.card} ${isBooked ? styles.cardBooked : ""}`}
    >
      <div className={styles.imgWrap}>
        {bike.images?.[0] ? (
          <img
            src={resolveImg(bike.images[0])}
            alt={bike.name}
            className={styles.img}
          />
        ) : (
          <div className={styles.placeholder}>
            <svg
              viewBox="0 0 120 60"
              fill="none"
              className={styles.placeholderIcon}
            >
              <circle
                cx="22"
                cy="44"
                r="14"
                stroke="currentColor"
                strokeWidth="4"
                fill="none"
              />
              <circle cx="22" cy="44" r="5" fill="currentColor" opacity=".4" />
              <circle
                cx="98"
                cy="44"
                r="14"
                stroke="currentColor"
                strokeWidth="4"
                fill="none"
              />
              <circle cx="98" cy="44" r="5" fill="currentColor" opacity=".4" />
              <path
                d="M36 44 L50 20 L72 20 L84 32 L98 30"
                stroke="currentColor"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M50 20 L42 44"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
              />
              <path
                d="M72 20 L72 12 L88 12"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M84 14 L92 14"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
              />
            </svg>
          </div>
        )}
        <div className={styles.imgOverlay} />

        {/* Prominent availability badge  top left */}
        <span
          className={`${styles.statusBadge} ${isBooked ? styles.statusBooked : styles.statusAvailable}`}
        >
          <span className={styles.statusDot} />
          {isBooked ? "Booked" : "Available"}
        </span>

        {/* Type chip  top right */}
        <span className={styles.typeChip}>
          {TYPE_ICONS[bike.type] || "🏍️"} {TYPE_LABELS[bike.type] || bike.type}
        </span>

        {/* Booked overlay stripe */}
        {isBooked && <div className={styles.bookedOverlay} />}
      </div>
      <div className={styles.body}>
        <div className={styles.cardTop}>
          <h3 className={styles.name}>{bike.name || "Unnamed"}</h3>
        </div>
        <p className={styles.brand}>{bike.brand || ""}</p>
        {rating > 0 && (
          <div className={styles.rating}>
            <span className={styles.stars}>
              {"★".repeat(stars)}
              {"☆".repeat(5 - stars)}
            </span>
            <span className={styles.ratingNum}>
              {rating.toFixed(1)}{" "}
              <span className={styles.ratingCount}>({reviews})</span>
            </span>
          </div>
        )}
        <div className={styles.footer}>
          <div className={styles.price}>
            <span className={styles.priceMain}>
              ₹{bike.price_per_day || 0}
              <small>/day</small>
            </span>
            <span className={styles.priceSub}>+ 18% GST</span>
          </div>
          {isBooked ? (
            <span className={styles.bookedBtn}>Unavailable</span>
          ) : (
            <span className={styles.bookBtn}>Book →</span>
          )}
        </div>
        <p className={styles.location}>📍 {bike.location || ""}</p>
      </div>
    </Link>
  );
};

export default BikesPage;
